import type { Record as SdkRecord, Factory, Diagnostics, Shape } from '@servicenow/sdk-build-core'
import { ObjectShape, parseGlideDuration } from '@servicenow/sdk-build-core'

// Inline implementation of formatDuration (available in sdk-build-core >=4.7.0)
function formatDuration(duration: { days?: number; hours?: number; minutes?: number; seconds?: number }): string {
    const days = duration.days ?? 0
    const hours = duration.hours ?? 0
    const minutes = duration.minutes ?? 0
    const seconds = duration.seconds ?? 0
    const totalMs = ((days * 24 + hours) * 60 + minutes) * 60 * 1000 + seconds * 1000
    const date = new Date(Date.UTC(1970, 0, 1) + totalMs)
    const Y = date.getUTCFullYear().toString().padStart(4, '0')
    const M = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    const D = date.getUTCDate().toString().padStart(2, '0')
    const h = date.getUTCHours().toString().padStart(2, '0')
    const m = date.getUTCMinutes().toString().padStart(2, '0')
    const s = date.getUTCSeconds().toString().padStart(2, '0')
    return `${Y}-${M}-${D} ${h}:${m}:${s}`
}
import {
    DURATION_TYPE_FROM_DB,
    DURATION_TYPE_TO_DB,
    TIMER_ATTRIBUTES_TABLE,
} from '../constants/playbook-plugin-constants'
import { type PillContext, resolvePlaybookPill } from '../utils/playbook-pill-resolver'
import { type PillTransformContext, transformStandardPills } from '../utils/playbook-pill-transformer'
/**
 * Find the timer attributes record matching a lane (or activity) from a list of descendants.
 * Matches on `source` = record sys_id and `source_type` = expected type.
 * Returns undefined if no matching active timer record exists.
 */
export function findTimerRecord(
    timerRecords: SdkRecord[],
    sourceId: string,
    sourceType: string
): SdkRecord | undefined {
    return timerRecords.find((timer) => {
        const source = timer.get('source')?.ifString()?.getValue()
        const type = timer.get('source_type')?.ifString()?.getValue()
        const active = timer.get('active')?.ifString()?.getValue()
        // Default active to true if not specified
        const isActive = active === undefined || active === '' || active === 'true'
        return source === sourceId && type === sourceType && isActive
    })
}

/**
 * Maps a sys_pd_timer_attributes DB record to a StartWithDelay ObjectShape
 * for inclusion in a lane/activity config shape.
 *
 * Switches on `duration_type` to include only the relevant fields.
 * Converts epoch-relative datetime strings (e.g. '1970-01-05 03:02:01')
 * to Duration objects ({ days: 4, hours: 3, minutes: 2, seconds: 1 }).
 *
 * When a pill context is provided, string fields containing standard pill
 * references (e.g. `{{pd.inputs.targetDate}}`) are transformed into
 * `wfa.playbook.dataPill()` expressions before any type-specific parsing.
 */
export function timerAttributesToShape(timerRecord: SdkRecord, pillContext?: PillTransformContext): ObjectShape {
    const durationType = timerRecord.get('duration_type')?.ifString()?.getValue() ?? ''
    const fluentType = DURATION_TYPE_FROM_DB[durationType]

    if (!fluentType) {
        // Unknown duration type — return empty shape
        return new ObjectShape({ source: timerRecord, properties: {} })
    }

    if (fluentType === 'explicit') {
        const timerDuration = timerRecord.get('timer_duration')?.ifString()?.getValue() ?? ''
        const durationPill = pillContext ? transformStandardPills(timerDuration, timerRecord, pillContext) : undefined
        if (durationPill) {
            return new ObjectShape({
                source: timerRecord,
                properties: { type: fluentType, duration: durationPill },
            })
        }

        const duration = parseGlideDuration(timerDuration)
        return new ObjectShape({
            source: timerRecord,
            properties: {
                type: fluentType,
                ...(duration ? { duration } : {}),
            },
        })
    }

    if (fluentType === 'relative') {
        const timerDuration = timerRecord.get('timer_duration')?.ifString()?.getValue() ?? ''
        const durationPill = pillContext ? transformStandardPills(timerDuration, timerRecord, pillContext) : undefined
        const duration = durationPill ?? parseGlideDuration(timerDuration)

        const rawRelativeDatetime = timerRecord.get('timer_relative_duration_datetime')?.ifString()?.getValue() ?? ''
        const relativeDatetimePill = pillContext
            ? transformStandardPills(rawRelativeDatetime, timerRecord, pillContext)
            : undefined

        return timerRecord.transform(({ $ }) => ({
            type: $.val(fluentType),
            ...(duration ? { duration: $.val(duration) } : {}),
            relativeDatetime: relativeDatetimePill
                ? $.val(relativeDatetimePill)
                : $.from('timer_relative_duration_datetime'),
            relativeOperator: $.from('timer_relative_duration_operator'),
        }))
    }

    // percentage
    const rawPercentage = timerRecord.get('timer_percentage')?.ifString()?.getValue() ?? ''
    const percentagePill = pillContext ? transformStandardPills(rawPercentage, timerRecord, pillContext) : undefined

    const rawPercentageDatetime = timerRecord.get('percentage_timer_input_datetime')?.ifString()?.getValue() ?? ''
    const percentageDatetimePill = pillContext
        ? transformStandardPills(rawPercentageDatetime, timerRecord, pillContext)
        : undefined

    return timerRecord.transform(({ $ }) => ({
        type: $.val(fluentType),
        percentage: percentagePill ? $.val(percentagePill) : $.from('timer_percentage').toNumber(),
        percentageDatetime: percentageDatetimePill
            ? $.val(percentageDatetimePill)
            : $.from('percentage_timer_input_datetime'),
    }))
}

/**
 * Creates a sys_pd_timer_attributes record from a startWithDelay config shape.
 *
 * Converts Duration objects ({ days: 4, hours: 3, minutes: 2, seconds: 1 })
 * to epoch-relative datetime strings (e.g. '1970-01-05 03:02:01').
 *
 * @param startWithDelayShape - The ObjectShape for the startWithDelay config
 * @param laneRecord - The parent lane/activity record (used for source FK)
 * @param processDefinition - The process_definition value (playbook sys_id)
 * @param sourceType - 'sys_pd_lane' or 'sys_pd_activity'
 * @param factory - Record factory
 * @param diagnostics - For validation errors
 * @param pillContext - Pill context for resolving dataPill() references in timer fields
 */
export async function timerAttributesToRecord(
    startWithDelayShape: ObjectShape,
    laneRecord: SdkRecord,
    processDefinition: string,
    sourceType: string,
    factory: Factory,
    diagnostics: Diagnostics,
    pillContext: PillContext
): Promise<SdkRecord | undefined> {
    const typeVal = startWithDelayShape.get('type')?.ifString()?.getValue()
    if (!typeVal) {
        return undefined
    }

    const dbDurationType = DURATION_TYPE_TO_DB[typeVal as keyof typeof DURATION_TYPE_TO_DB]
    if (!dbDurationType) {
        return undefined
    }

    // Validate percentage range (only when the value is a literal, not a pill)
    if (typeVal === 'percentage') {
        const pctShape = startWithDelayShape.get('percentage')
        const pctVal = pctShape?.ifNumber()?.getValue()
        if (pctShape && pctVal !== undefined && (pctVal <= 0 || pctVal > 100)) {
            diagnostics.error(pctShape, 'percentage must be greater than 0 and at most 100')
        }
    }

    const properties: Record<string, string | Shape> = {
        active: 'true',
        duration_type: dbDurationType,
        source: laneRecord.getId().getValue(),
        source_type: sourceType,
        process_definition: processDefinition,
    }

    if (typeVal === 'explicit' || typeVal === 'relative') {
        const durationShape = startWithDelayShape.get('duration')
        if (durationShape) {
            const resolvedDuration = await resolvePlaybookPill(durationShape, pillContext)
            // resolvePlaybookPill returns the original ObjectShape unchanged for
            // non-pill values, so ifObject() succeeds for literal Duration objects.
            // For pill references the resolver produces a StringShape
            // (DeferredPillShape), which is never an object, so ifObject() returns
            // undefined and we fall through.
            const durationObj = resolvedDuration.ifObject()
            if (durationObj) {
                properties['timer_duration'] = formatDuration({
                    days: durationObj.get('days')?.ifNumber()?.getValue() ?? 0,
                    hours: durationObj.get('hours')?.ifNumber()?.getValue() ?? 0,
                    minutes: durationObj.get('minutes')?.ifNumber()?.getValue() ?? 0,
                    seconds: durationObj.get('seconds')?.ifNumber()?.getValue() ?? 0,
                })
            } else if (!resolvedDuration.isUndefined()) {
                properties['timer_duration'] = resolvedDuration
            }
        }
    }
    if (typeVal === 'relative') {
        const relativeDatetimeShape = startWithDelayShape.get('relativeDatetime')
        if (relativeDatetimeShape) {
            properties['timer_relative_duration_datetime'] = await resolvePlaybookPill(
                relativeDatetimeShape,
                pillContext
            )
        }
        properties['timer_relative_duration_operator'] =
            startWithDelayShape.get('relativeOperator')?.ifString()?.getValue() ?? ''
    }
    if (typeVal === 'percentage') {
        const percentageShape = startWithDelayShape.get('percentage')
        if (percentageShape) {
            const resolvedPercentage = await resolvePlaybookPill(percentageShape, pillContext)
            const pctNum = resolvedPercentage.ifNumber()?.getValue()
            if (pctNum !== undefined) {
                properties['timer_percentage'] = String(pctNum)
            } else if (!resolvedPercentage.isUndefined()) {
                properties['timer_percentage'] = resolvedPercentage
            } else {
                properties['timer_percentage'] = ''
            }
        } else {
            properties['timer_percentage'] = ''
        }

        const percentageDatetimeShape = startWithDelayShape.get('percentageDatetime')
        if (percentageDatetimeShape) {
            properties['percentage_timer_input_datetime'] = await resolvePlaybookPill(
                percentageDatetimeShape,
                pillContext
            )
        }
    }

    return factory.createRecord({
        source: startWithDelayShape,
        table: TIMER_ATTRIBUTES_TABLE,
        properties,
    })
}
